/*
 * component-ctl-quick-add.js
 * Complete the Look quick-add tab + drawer for product pages (mobile only).
 * Markup: snippets/ctl-quick-add.liquid. Styles: component-ctl-quick-add.css.
 *
 *  - The tab docks above the sticky Add to cart bar (#ProductStickyCta).
 *    Tap it or swipe it up to open the drawer. Swipe the drawer header down,
 *    tap the dimmed page, tap X or press Escape to close.
 *  - In the drawer customers only pick sizes (tap again to un-pick). There
 *    are no per-item add buttons: while the drawer is open the sticky bar's
 *    own ADD TO CART adds just the picked pieces, reads "ADD 2 TO CART", and
 *    Buy now is hidden. When the drawer closes the bar goes back to adding the
 *    main product exactly as before (its own click handler is untouched).
 *  - The tab stays on screen the whole time on mobile, including while the
 *    real Complete the Look section is in view (changed 2026-10-13; it used
 *    to hide there).
 *  - Sold-out sizes can be picked too (one at a time, it clears other picks).
 *    While one is picked the sticky button reads CREATE AN ALERT, and tapping
 *    it opens the branded STOQ "Notify me" popup for THAT piece and size via
 *    window._RestockRocket.openModal(product, variantId). window.ecNmOverride
 *    tells component-restock-modal.js to show the piece's photo/name/price;
 *    it is cleared again when the popup closes (2026-10-14).
 *  - Adding posts to /cart/add.js with the same section ids the theme's
 *    <product-form> uses, lets <w3-cart> re-render the cart drawer and
 *    bubble, then opens the cart drawer like the normal Add to cart does.
 */
(function () {
  if (typeof window === 'undefined' || window.__ctlqaInit) return;
  window.__ctlqaInit = true;

  var mq = window.matchMedia('(max-width: 1023.98px)');

  function init() {
    var root = document.getElementById('CtlQuickAdd');
    if (!root) return;

    // Move to <body> so no transformed ancestor can break position: fixed.
    if (root.parentNode !== document.body) document.body.appendChild(root);

    var tab = root.querySelector('.js-ctlqa-tab');
    var sheet = root.querySelector('.ctlqa__sheet');
    var head = root.querySelector('.js-ctlqa-head');
    var closeBtn = root.querySelector('.ctlqa__close');
    var errEl = root.querySelector('.js-ctlqa-error');
    var bar = document.getElementById('ProductStickyCta');
    var stickyAdd = bar ? bar.querySelector('.js-sticky-add-to-cart') : null;
    var isOpen = false;
    var busy = false;
    var saved = null; // sticky Add button state saved while the drawer is open

    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg || '';
      errEl.hidden = !msg;
    }

    // Dock height follows the real sticky bar (0 if the bar isn't shown).
    function syncDock() {
      var h = 0;
      if (bar && getComputedStyle(bar).display !== 'none') h = Math.round(bar.getBoundingClientRect().height);
      root.style.setProperty('--ctlqa-dock', h + 'px');
    }
    syncDock();
    window.addEventListener('resize', syncDock);
    // The bar's height can change (e.g. the delivery-date line inside it
    // shows or hides), so keep the tab docked right on top of it.
    if (bar && 'ResizeObserver' in window) new ResizeObserver(syncDock).observe(bar);

    // ---- picked pieces ----
    function picked() {
      var ids = [];
      root.querySelectorAll('.ctlqa__size[aria-pressed="true"]:not(.is-out)').forEach(function (b) {
        ids.push(Number(b.getAttribute('data-variant-id')));
      });
      return ids;
    }
    // a picked sold-out size, if any (restock alert mode)
    function alertPick() {
      return root.querySelector('.ctlqa__size.is-out[aria-pressed="true"]');
    }

    function stickyLabel(text) {
      if (!stickyAdd) return;
      var span = stickyAdd.querySelector('span');
      if (span) span.textContent = text; else stickyAdd.textContent = text;
    }

    function syncSticky() {
      if (!stickyAdd || !isOpen) return;
      if (alertPick()) {
        stickyLabel('CREATE AN ALERT');
        stickyAdd.classList.remove('is-ctlqa-empty');
        return;
      }
      var n = picked().length;
      stickyLabel(n ? 'ADD ' + n + ' TO CART' : 'ADD TO CART');
      stickyAdd.classList.toggle('is-ctlqa-empty', n === 0);
    }

    // ---- open / close ----
    function setOpen(open) {
      if (open === isOpen) return;
      isOpen = open;
      root.classList.toggle('is-open', open);
      document.body.classList.toggle('ctlqa-open', open);
      sheet.setAttribute('aria-hidden', open ? 'false' : 'true');
      tab.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.documentElement.style.overflow = open ? 'hidden' : '';
      showError('');

      if (stickyAdd) {
        if (open) {
          // Take over the sticky Add button. It may be disabled because the
          // main product is sold out; the picked pieces can still be added.
          saved = { html: stickyAdd.innerHTML, disabled: stickyAdd.disabled, aria: stickyAdd.getAttribute('aria-disabled') };
          stickyAdd.disabled = false;
          stickyAdd.removeAttribute('aria-disabled');
          syncSticky();
        } else if (saved) {
          stickyAdd.innerHTML = saved.html;
          stickyAdd.disabled = saved.disabled;
          if (saved.aria !== null) stickyAdd.setAttribute('aria-disabled', saved.aria); else stickyAdd.removeAttribute('aria-disabled');
          stickyAdd.classList.remove('is-ctlqa-empty', 'is-ctlqa-loading');
          saved = null;
        }
      }

      if (open) {
        setTimeout(function () { closeBtn && closeBtn.focus({ preventScroll: true }); }, 50);
      } else if (document.activeElement && sheet.contains(document.activeElement)) {
        tab.focus({ preventScroll: true });
      }
    }

    function render() {
      var show = mq.matches;
      root.classList.toggle('is-tab-on', show);
      document.body.classList.toggle('ctlqa-tab-on', show);
      if (!show) setOpen(false);
    }

    if (mq.addEventListener) mq.addEventListener('change', render);
    render();

    // ---- open: tap or swipe up on the tab ----
    var startY = null;
    var swiped = false;
    tab.addEventListener('pointerdown', function (e) {
      startY = e.clientY; swiped = false;
      try { tab.setPointerCapture(e.pointerId); } catch (err) {}
    });
    tab.addEventListener('pointermove', function (e) {
      if (startY === null) return;
      if (startY - e.clientY > 22) { swiped = true; startY = null; setOpen(true); }
    });
    tab.addEventListener('pointerup', function () { startY = null; });
    tab.addEventListener('pointercancel', function () { startY = null; });
    tab.addEventListener('click', function () {
      if (swiped) { swiped = false; return; }
      setOpen(true);
    });

    // ---- close: swipe header down, scrim, X, Escape ----
    var downY = null;
    head.addEventListener('pointerdown', function (e) {
      if (e.target.closest('button')) return;
      downY = e.clientY;
      try { head.setPointerCapture(e.pointerId); } catch (err) {}
    });
    head.addEventListener('pointermove', function (e) {
      if (downY !== null && e.clientY - downY > 22) { downY = null; setOpen(false); }
    });
    head.addEventListener('pointerup', function () { downY = null; });
    head.addEventListener('pointercancel', function () { downY = null; });
    root.querySelectorAll('.js-ctlqa-close').forEach(function (el) {
      el.addEventListener('click', function () { setOpen(false); });
    });
    document.addEventListener('keyup', function (e) {
      if (isOpen && (e.key === 'Escape' || e.keyCode === 27)) setOpen(false);
    });

    // ---- back-in-stock alerts for sold-out pieces (STOQ popup) ----
    var productCache = {};
    function getProduct(handle) {
      if (!productCache[handle]) {
        var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        productCache[handle] = fetch(rootUrl + 'products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); });
      }
      return productCache[handle];
    }

    function clearOverrideOnClose() {
      var seen = false;
      var mo = new MutationObserver(function () {
        var fc = document.getElementById('formContainer');
        if (fc) { seen = true; return; }
        if (seen) { window.ecNmOverride = null; mo.disconnect(); }
      });
      mo.observe(document.body, { childList: true, subtree: true });
      // safety net: never leave the override behind for the page's own popup
      setTimeout(function () { if (!seen) { window.ecNmOverride = null; mo.disconnect(); } }, 8000);
    }

    function openNotify(item, variantId) {
      var rr = window._RestockRocket || window._Stoq;
      var url = item.getAttribute('data-url');
      if (!rr || typeof rr.openModal !== 'function') { if (url) window.location.href = url; return; }
      var prices = {};
      item.querySelectorAll('.ctlqa__size[data-variant-id]').forEach(function (s) {
        if (s.getAttribute('data-price')) prices[s.getAttribute('data-variant-id')] = s.getAttribute('data-price');
      });
      getProduct(item.getAttribute('data-handle')).then(function (p) {
        window.ecNmOverride = {
          title: item.getAttribute('data-title') || p.title,
          image: item.getAttribute('data-image') || '',
          price: prices[variantId] || item.getAttribute('data-price') || '',
          prices: prices
        };
        clearOverrideOnClose();
        rr.openModal(p, Number(variantId));
      }).catch(function () { if (url) window.location.href = url; });
    }

    // ---- size picking (one size per piece, tap again to un-pick) ----
    root.querySelectorAll('.js-ctlqa-item').forEach(function (item) {
      var sizes = item.querySelector('.js-ctlqa-sizes');
      var price = item.querySelector('.js-ctlqa-price');
      if (!sizes) return;
      sizes.addEventListener('click', function (e) {
        var b = e.target.closest('.ctlqa__size');
        if (!b || b.disabled) return;
        var wasOn = b.getAttribute('aria-pressed') === 'true';
        var isOut = b.classList.contains('is-out');
        if (!wasOn) {
          // A sold-out pick is an alert for one piece, so it can't be mixed with
          // add-to-cart picks: picking one clears every other pick, and picking
          // an in-stock size clears any sold-out pick.
          var clear = isOut ? root.querySelectorAll('.ctlqa__size[aria-pressed="true"]')
                            : root.querySelectorAll('.ctlqa__size.is-out[aria-pressed="true"]');
          clear.forEach(function (x) {
            x.setAttribute('aria-pressed', 'false');
            var it = x.closest('.js-ctlqa-item');
            if (it) it.classList.remove('is-picked');
          });
        }
        sizes.querySelectorAll('.ctlqa__size').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
        if (!wasOn) b.setAttribute('aria-pressed', 'true');
        item.classList.toggle('is-picked', !wasOn);
        if (!wasOn && price && b.getAttribute('data-price')) {
          var cmp = b.getAttribute('data-compare');
          price.innerHTML = (cmp ? '<s class="ctlqa__compare">' + cmp + '</s>' : '') + b.getAttribute('data-price');
        }
        showError('');
        syncSticky();
      });
    });

    // ---- sticky ADD TO CART while the drawer is open ----
    // Capture phase on document runs before the sticky bar's own click
    // handler, so we can take the click only while the drawer is open.
    document.addEventListener('click', function (e) {
      if (!isOpen || !stickyAdd) return;
      if (!e.target.closest || e.target.closest('.js-sticky-add-to-cart') !== stickyAdd) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (busy) return;

      var oos = alertPick();
      if (oos) {
        showError('');
        openNotify(oos.closest('.js-ctlqa-item'), oos.getAttribute('data-variant-id'));
        return;
      }

      var ids = picked();
      if (!ids.length) {
        showError('Pick a size on the pieces you want first.');
        root.querySelectorAll('.js-ctlqa-sizes').forEach(function (s) {
          s.classList.remove('is-nudge'); void s.offsetWidth; s.classList.add('is-nudge');
        });
        return;
      }

      busy = true;
      showError('');
      stickyAdd.classList.add('is-ctlqa-loading');
      stickyLabel('ADDING...');

      var cart = document.querySelector('w3-cart');
      var sectionIds = [];
      if (cart && typeof cart.getSectionsToRender === 'function') {
        cart.getSectionsToRender().forEach(function (s) { if (sectionIds.indexOf(s.id) === -1) sectionIds.push(s.id); });
      }
      var body = { items: ids.map(function (id) { return { id: id, quantity: 1 }; }) };
      if (sectionIds.length) { body.sections = sectionIds.join(','); body.sections_url = window.location.pathname; }

      var addUrl = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          busy = false;
          stickyAdd.classList.remove('is-ctlqa-loading');
          if (data && data.status) {
            showError(data.description || data.message || 'Could not add these pieces.');
            syncSticky();
            return;
          }
          try { if (cart && data.sections) cart.updateCart(data); } catch (err) { /* cart UI refresh is best effort */ }
          document.dispatchEvent(new CustomEvent('theme:cart:update'));
          // reset picks, close, and open the cart drawer like the normal Add to cart
          root.querySelectorAll('.ctlqa__size[aria-pressed="true"]').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
          root.querySelectorAll('.js-ctlqa-item.is-picked').forEach(function (it) { it.classList.remove('is-picked'); });
          setOpen(false);
          document.dispatchEvent(new CustomEvent('theme:drawer:cart:open'));
        })
        .catch(function () {
          busy = false;
          stickyAdd.classList.remove('is-ctlqa-loading');
          showError('Something went wrong. Please try again.');
          syncSticky();
        });
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
